import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldAlert, ScanSearch, Lock, Unlock, RefreshCw, Loader2 } from "lucide-react";

const TABS = [
  { key: "events", label: "Sự kiện bảo mật", icon: ShieldAlert },
  { key: "lookups", label: "Nhật ký tra cứu", icon: ScanSearch },
  { key: "blocked", label: "Đang bị khóa", icon: Lock },
];

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("vi-VN", { hour12: false });
  } catch {
    return iso;
  }
}

export default function SecurityLog() {
  const [tab, setTab] = useState("events");
  const [events, setEvents] = useState([]);
  const [lookups, setLookups] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, l, b] = await Promise.all([
        api.get("/admin/security-events", { params: { limit: 300 } }),
        api.get("/admin/lookup-logs", { params: { limit: 300 } }),
        api.get("/admin/blocked-users"),
      ]);
      setEvents(e.data);
      setLookups(l.data);
      setBlocked(b.data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const unblock = async (uid) => {
    try {
      await api.post(`/admin/users/${uid}/unblock`);
      toast.success("Đã gỡ khóa tra cứu");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="security-log-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-black text-white flex items-center gap-3">
            <ShieldAlert className="w-8 h-8 text-primary" /> Nhật ký & Bảo mật
          </h1>
          <p className="text-zinc-500 text-sm mt-2">
            Theo dõi tra cứu bất thường, chống cào dữ liệu và các sự kiện vi phạm.
          </p>
        </div>
        <button
          data-testid="refresh-security-btn"
          onClick={load}
          className="flex items-center gap-2 border border-zinc-700 text-zinc-300 hover:text-white hover:border-primary rounded-sm px-4 py-2 text-sm transition-colors"
        >
          <RefreshCw className="w-4 h-4" /> Làm mới
        </button>
      </div>

      <div className="flex gap-2 border-b border-zinc-800">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          const count = t.key === "events" ? events.length : t.key === "lookups" ? lookups.length : blocked.length;
          return (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active ? "text-white border-primary" : "text-zinc-500 border-transparent hover:text-zinc-300"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
              <span className="text-xs bg-white/10 rounded-full px-2 py-0.5">{count}</span>
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-zinc-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Đang tải...
        </div>
      )}

      {tab === "blocked" && (
        <div className="space-y-3" data-testid="blocked-list">
          {blocked.length === 0 && !loading && (
            <div className="text-zinc-500 text-sm bg-card border border-zinc-800 rounded-sm p-6 text-center">
              Không có tài khoản nào đang bị khóa tra cứu.
            </div>
          )}
          {blocked.map((u) => (
            <div key={u.id} data-testid={`blocked-${u.id}`} className="bg-card border border-red-500/40 rounded-sm p-4 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-white font-semibold">{u.company_name || u.name}</div>
                <div className="text-xs text-zinc-500">{u.phone} · Khóa đến {fmt(u.lookup_block_until)}</div>
              </div>
              <button
                data-testid={`unblock-${u.id}`}
                onClick={() => unblock(u.id)}
                className="flex items-center gap-2 bg-primary text-white rounded-sm px-4 py-2 text-sm hover:bg-primary/90 transition-colors"
              >
                <Unlock className="w-4 h-4" /> Gỡ khóa
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === "events" && (
        <div className="overflow-x-auto bg-card border border-zinc-800 rounded-sm" data-testid="events-list">
          <table className="w-full text-sm">
            <thead className="text-zinc-500 border-b border-zinc-800">
              <tr className="text-left">
                <th className="p-3">Thời gian</th>
                <th className="p-3">Loại</th>
                <th className="p-3">Chi tiết</th>
                <th className="p-3">Tài khoản</th>
                <th className="p-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} className="border-b border-zinc-900 hover:bg-white/5">
                  <td className="p-3 text-zinc-400 whitespace-nowrap">{fmt(e.created_at)}</td>
                  <td className="p-3"><span className="text-red-400 font-mono text-xs">{e.type}</span></td>
                  <td className="p-3 text-zinc-300 max-w-md">{e.detail || "—"}</td>
                  <td className="p-3 text-zinc-300">{e.name} <span className="text-zinc-600">/ {e.company}</span></td>
                  <td className="p-3 text-zinc-500 font-mono text-xs">{e.ip}</td>
                </tr>
              ))}
              {events.length === 0 && !loading && (
                <tr><td colSpan={5} className="p-6 text-center text-zinc-500">Chưa có sự kiện bảo mật.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "lookups" && (
        <div className="overflow-x-auto bg-card border border-zinc-800 rounded-sm" data-testid="lookups-list">
          <table className="w-full text-sm">
            <thead className="text-zinc-500 border-b border-zinc-800">
              <tr className="text-left">
                <th className="p-3">Thời gian</th>
                <th className="p-3">Tài khoản</th>
                <th className="p-3">Tra cứu</th>
                <th className="p-3">Nội dung</th>
                <th className="p-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {lookups.map((l) => (
                <tr key={l.id} className="border-b border-zinc-900 hover:bg-white/5">
                  <td className="p-3 text-zinc-400 whitespace-nowrap">{fmt(l.created_at)}</td>
                  <td className="p-3 text-zinc-300">{l.name} <span className="text-zinc-600">/ {l.company}</span></td>
                  <td className="p-3 text-zinc-500 font-mono text-xs">{l.endpoint}</td>
                  <td className="p-3 text-zinc-300">{l.query || "—"}</td>
                  <td className="p-3 text-zinc-500 font-mono text-xs">{l.ip}</td>
                </tr>
              ))}
              {lookups.length === 0 && !loading && (
                <tr><td colSpan={5} className="p-6 text-center text-zinc-500">Chưa có lượt tra cứu nào.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
